import { Trans, useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import {
  Item,
  ItemActions,
  ItemContent,
  ItemMedia,
  ItemMediaFrame,
  ItemMetaField,
  ItemMetaFieldText,
  ItemMetaRow,
  ItemSnippet,
  ItemTitle,
  ItemTitleText,
  ItemTrigger,
} from 'designSystem/components/Item'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'designSystem/components/Tooltip'
import { useIsTouchDevice } from 'designSystem/hooks/useIsTouchDevice'
import {
  type DocumentTableRow,
  formatDocumentDisplayTitle,
} from 'features/knowledgeDocumentViewer'
import { DocumentThumbnail } from 'features/knowledgeDocumentViewer/'
import { DownloadSearchResultButton } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsSearchActions/DownloadSearchResultButton'
import { type DrawingRevisionOption } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import { revisionOptionClassification } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/drawingRevisionOptions'
import { RevisionFamilyDropdown } from 'features/knowledgeDocumentViewer/components/metadata/RevisionFamilyDropdown'
import {
  formatRevisionLabel,
  hasNewerRevision,
  type RevisionMenuItemView,
  type RevisionStamp,
} from 'features/knowledgeDocumentViewer/utils/revisionHelpers'
import { formatDrawingMetadata } from 'features/knowledgeDocumentViewer/utils/textHelpers'
import { WorkOrderCardMetadata } from 'features/knowledgeDocumentViewer/workOrders/WorkOrderCardMetadata'
import { ExternalLink, FileDigit, Info, Star, Store } from 'lucide-react'
import React from 'react'
import styled from 'styled-components'

import { revisionOptionKey } from './documentResultCardItems'

const SUMMARY_MAX_LENGTH = 240

const RevisionMenuWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
`

const CardItem = styled(Item)`
  position: relative;
`

const CardActions = styled(ItemActions)`
  position: absolute;
  z-index: 1;
  top: 0.75rem;
  right: 0.75rem;

  [data-size='compact'] & {
    top: 0.5rem;
    right: 0.5rem;
  }
`

const CardTitle = styled(ItemTitle)<{ $actionSpace: string }>`
  padding-right: ${({ $actionSpace }) => $actionSpace};
`

interface DocumentResultCardProps {
  document: DocumentTableRow
  isCompact?: boolean
  onClick: () => void
  onDoubleClick?: () => void
  onOpenInNewTab?: () => void
  onPointerDownCapture?: React.PointerEventHandler<HTMLButtonElement>
  cardRef?: (el: HTMLButtonElement | null) => void
  favoritesDisabled?: boolean
  favoritesSaving?: boolean
  isFavorited?: boolean
  onToggleFavorite?: (document: DocumentTableRow) => void
  pageNumber?: number
  thumbnailBqDocumentId?: string
  isPageCard?: boolean
  drawingNumber?: string
  sheetNumber?: string
  revisionCode?: string
  revisionStackingFailed?: boolean
  revisionOptions?: DrawingRevisionOption[]
  selectedRevisionKey?: string
  onRevisionChange?: (option: DrawingRevisionOption) => void
  siteId?: string
  showDownload?: boolean
}

function truncateSummary(summary: string): string {
  if (summary.length <= SUMMARY_MAX_LENGTH) return summary
  return summary.slice(0, SUMMARY_MAX_LENGTH).trimEnd() + '…'
}

export const DocumentResultCard: React.FC<DocumentResultCardProps> = ({
  document,
  isCompact = false,
  onClick,
  onDoubleClick,
  onOpenInNewTab,
  onPointerDownCapture,
  cardRef,
  isFavorited = false,
  favoritesDisabled = false,
  favoritesSaving = false,
  onToggleFavorite,
  pageNumber,
  thumbnailBqDocumentId,
  isPageCard = false,
  drawingNumber,
  sheetNumber,
  revisionCode,
  revisionStackingFailed = false,
  revisionOptions,
  selectedRevisionKey,
  onRevisionChange,
  siteId,
  showDownload = true,
}) => {
  const { t } = useLingui()
  const rawDisplayTitle = document.documentTitle || document.title
  const formattedDocumentTitle = formatDocumentDisplayTitle(
    document.documentTitle,
    document.title
  )
  const documentDrawingNumber =
    drawingNumber ||
    document.drawing ||
    (rawDisplayTitle !== document.title ? document.title : null)
  const displayTitle = formattedDocumentTitle
  const displaySecondaryTitle = formatDrawingMetadata(
    documentDrawingNumber,
    sheetNumber
  )
  const summarySnippet = document.summary
    ? truncateSummary(document.summary)
    : null
  const hasRevisionOptions = (revisionOptions?.length ?? 0) > 1
  const hasMeta = Boolean(document.vendor) || Boolean(displaySecondaryTitle)
  const selectedRevision = revisionOptions?.find(
    (option) => revisionOptionKey(option) === selectedRevisionKey
  )
  const latestRevision = revisionOptions?.[0]
  const displayedRevision = selectedRevision ?? latestRevision
  const currentRevisionKey =
    selectedRevisionKey ?? (latestRevision && revisionOptionKey(latestRevision))
  const revisionTriggerLabel = formatRevisionLabel(
    revisionOptionClassification(displayedRevision)?.revision_code ??
      revisionCode
  )
  const isOutdatedRevision = hasNewerRevision(
    revisionOptions?.map(revisionOptionStamp) ?? [],
    selectedRevisionKey
  )
  const isTouchDevice = useIsTouchDevice()
  const showOpenInNewTab = isTouchDevice && Boolean(onOpenInNewTab)
  const cardSize = isCompact ? 'compact' : 'lg'
  const hasRevisionAction = hasRevisionOptions || revisionStackingFailed
  const favoriteLabel = isFavorited
    ? t`Remove from favorites`
    : t`Add to favorites`
  const openInNewTabLabel = t`Open in new tab`
  const titleActionSpace = getTitleActionSpace({
    hasRevisionAction,
    iconActionCount:
      (showDownload ? 1 : 0) +
      (onToggleFavorite ? 1 : 0) +
      (showOpenInNewTab ? 1 : 0),
  })

  return (
    <CardItem size={cardSize} variant='surface'>
      <ItemTrigger
        ref={cardRef}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        onPointerDownCapture={onPointerDownCapture}>
        <ItemMedia variant='thumbnail'>
          <ItemMediaFrame>
            {(!isPageCard || pageNumber !== undefined) && (
              <DocumentThumbnail
                siteId={siteId}
                documentId={document.systemEntryId}
                alt={displayTitle}
                filename={document.filename}
                bqDocumentId={
                  thumbnailBqDocumentId ?? document.bqDocumentId ?? document.id
                }
                cantileverDocumentEntryId={document.cantileverDocumentEntryId}
                documentHostsHydrated
                maxDimensionRem={isCompact ? 4 : 6}
                pageNumber={pageNumber ?? 1}
              />
            )}
          </ItemMediaFrame>
        </ItemMedia>
        <ItemContent variant='document'>
          <CardTitle variant='document' $actionSpace={titleActionSpace}>
            <ItemTitleText>{displayTitle}</ItemTitleText>
            {isPageCard && pageNumber !== undefined && (
              <ItemTitleText tone='muted' spacing='leading' truncate={false}>
                <Trans>p.{pageNumber}</Trans>
              </ItemTitleText>
            )}
          </CardTitle>
          {document.cmms ? <WorkOrderCardMetadata {...document.cmms} /> : null}
          {summarySnippet && <ItemSnippet>{summarySnippet}</ItemSnippet>}
          {hasMeta && !document.cmms && (
            <ItemMetaRow gap='relaxed'>
              {document.vendor && (
                <ItemMetaField>
                  <Store strokeWidth={1.75} />
                  <ItemMetaFieldText>{document.vendor}</ItemMetaFieldText>
                </ItemMetaField>
              )}
              {displaySecondaryTitle && (
                <ItemMetaField>
                  <FileDigit strokeWidth={1.75} />
                  <ItemMetaFieldText>{displaySecondaryTitle}</ItemMetaFieldText>
                </ItemMetaField>
              )}
            </ItemMetaRow>
          )}
        </ItemContent>
      </ItemTrigger>
      <CardActions variant='titleTrailing'>
        {hasRevisionOptions && revisionOptions && (
          <RevisionMenuWrapper onClick={(event) => event.stopPropagation()}>
            <RevisionFamilyDropdown
              revisions={revisionOptions}
              toMenuItem={(option) =>
                revisionOptionMenuItem(option, currentRevisionKey)
              }
              isOutdated={isOutdatedRevision}
              triggerLabel={revisionTriggerLabel}
              outdatedTooltip={t`This revision is out of date`}
              size='xs'
              onSelectRevision={(option) => onRevisionChange?.(option)}
            />
          </RevisionMenuWrapper>
        )}
        {!hasRevisionOptions && revisionStackingFailed && (
          <RevisionStackingFailedIndicator
            revisionLabel={revisionTriggerLabel}
          />
        )}
        {showDownload && (
          <DownloadSearchResultButton
            document={document}
            pageNumber={isPageCard ? pageNumber : undefined}
            size='icon-xs'
          />
        )}
        {showOpenInNewTab && (
          <Button
            type='button'
            variant='inputRaised'
            size='icon-xs'
            title={openInNewTabLabel}
            aria-label={openInNewTabLabel}
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              onOpenInNewTab?.()
            }}>
            <ExternalLink size={16} />
          </Button>
        )}
        {onToggleFavorite && (
          <Button
            type='button'
            variant='inputRaised'
            size='icon-xs'
            title={favoriteLabel}
            aria-label={favoriteLabel}
            disabled={favoritesDisabled}
            aria-busy={favoritesSaving}
            aria-pressed={isFavorited}
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              onToggleFavorite(document)
            }}>
            <Star size={16} fill={isFavorited ? 'currentColor' : 'none'} />
          </Button>
        )}
      </CardActions>
    </CardItem>
  )
}

function getTitleActionSpace({
  hasRevisionAction,
  iconActionCount,
}: {
  hasRevisionAction: boolean
  iconActionCount: number
}): string {
  const iconSpaceRem = 1.5 * iconActionCount
  return `${hasRevisionAction ? iconSpaceRem + 6 : iconSpaceRem}rem`
}

function RevisionStackingFailedIndicator({
  revisionLabel,
}: {
  revisionLabel: string
}): React.ReactElement {
  const { t } = useLingui()
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type='button'
          variant='inputRaised'
          size='xs'
          aria-label={t`Revision stacking failed: duplicate titles or dates`}
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
          }}>
          {revisionLabel}
          <Info />
        </Button>
      </TooltipTrigger>
      <TooltipContent side='top' sideOffset={8} zIndex='overlay'>
        <Trans>
          Revision stacking failed:
          <br />
          duplicate titles or dates
        </Trans>
      </TooltipContent>
    </Tooltip>
  )
}

function revisionOptionStamp(option: DrawingRevisionOption): RevisionStamp {
  const classification = revisionOptionClassification(option)
  return {
    key: revisionOptionKey(option),
    code: classification?.revision_code,
    date: classification?.revision_date,
  }
}

function revisionOptionMenuItem(
  option: DrawingRevisionOption,
  currentKey: string | undefined
): RevisionMenuItemView {
  const stamp = revisionOptionStamp(option)
  return { ...stamp, isCurrent: stamp.key === currentKey, isSelectable: true }
}
